import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

import Accordion from 'components/Accordion';
import { Patamar as PatamarType } from 'helper/windx';
import {
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxPatamar,
  selectWindxUnidade,
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxIdsSimulacoesAntigasAtivas,
  selectWindxTelaAtiva,
  selectWindxFiltrosCompletos,
  selectWindxFatorRen,
  setIdsModelos,
  setOpcoesRmv,
  setTipoRodada,
  setPatamar,
  setFatorRen,
  setIdSubdivisaoEspacial,
  realizarConsulta,
  resetWindxControlPanel,
} from 'redux/reducers/windxControlPanelReducer';
import useEstimativaWindx from 'helper/windx/useEstimativaWindx';
import useControlPanelRules from 'hooks/Windx/useControlPanelRules';

import SavedPreferences from './SavedPreferences';
import MedidorComplexidade from './MedidorComplexidade';
import SimulacoesAntigasSalvas from './SimulacoesAntigasSalvas';
import EstudoAutomatico, { EVENT_KEY_ESTUDO_AUTOMATICO } from './EstudoAutomatico';
import Models from './Models';
import Rmv from './Rmv';
import RunType from './RunType';
import Patamar from './Patamar';
import FatorRen from './FatorRen';
import Actions from './Actions';

export default function ControlPanel() {
  const dispatch = useDispatch();
  const [showSaveForm, setShowSaveForm] = useState(false);

  // Regras de produto (o que aparece em cada tela, modelos visiveis, RMV
  // disponivel, janela de datas) vivem em hooks/Windx/useControlPanelRules,
  // compartilhado com o painel mobile. Ver ADR do spec Windx mobile (D5).
  const {
    opts, isLoading, error, tela,
    exibeRunType, exibeModelosRmv, exibePatamar, exibeFatorRen,
    exibeSimulacoesAntigas, abrirEstudoAutomatico, modoSelecao,
    modelosVisiveis, idsSemDados, datasRodadaPorId, opcoesRmvDisponiveis,
    dataMinima, dataMaxima,
  } = useControlPanelRules();

  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const patamar = useSelector(selectWindxPatamar);
  const fatorRen = useSelector(selectWindxFatorRen);
  const unidade = useSelector(selectWindxUnidade);
  const idSubdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const idsSimulacoesAntigasAtivas = useSelector(selectWindxIdsSimulacoesAntigasAtivas);
  const filtrosCompletos = useSelector(selectWindxFiltrosCompletos);

  // WINDX-051: medidor de complexidade (pré-voo). Em WEOL-SH, estima o custo
  // da seleção (debounce ~300ms) para avisar (amarelo) ou bloquear (vermelho)
  // antes do envio. Fora de SH, `estimativa` é undefined (sem medidor/gate).
  const { estimativa, isFetching: estimativaFetching } = useEstimativaWindx();

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" role="status" size="sm" />
        <span className="ms-2">Carregando opções do painel...</span>
      </div>
    );
  }

  if (error || !opts) {
    return (
      <Alert variant="danger" className="m-3">
        Não foi possível carregar as opções do painel. Tente novamente em instantes.
      </Alert>
    );
  }

  return (
    <div>
      {/* "Estudo Automatico" abre aberto sem a etapa Tipo de Rodada
          (`abrirEstudoAutomatico`, useControlPanelRules). O accordion nao e
          controlado e o painel fica montado entre as abas: a `key` o remonta
          quando essa condicao muda (troca SH <-> SM com a flag do BE
          desligada), para o `defaultActiveKey` valer de novo. Semanal <->
          Comparativo nao muda a condicao e mantem o que o usuario abriu. */}
      <Accordion
        key={abrirEstudoAutomatico ? 'estudo-automatico-aberto' : 'padrao'}
        defaultActiveKey={abrirEstudoAutomatico ? EVENT_KEY_ESTUDO_AUTOMATICO : undefined}
      >
        <SavedPreferences
          tela={tela}
          showSaveForm={showSaveForm}
          onCloseSaveForm={() => setShowSaveForm(false)}
          snapshot={{
            idsModelos,
            opcoesRmv,
            tipoRodada,
            patamar,
            unidade,
            idSubdivisao,
            idsEntidades,
            idsSimulacoesAntigasAtivas,
            fatorRen,
          }}
        />

        {/* WINDX-035: Tipo Rodada + Modelos + RMV ficam dentro do accordion
            pai "Estudo Automatico" — desde o WEOL-SM (#99) nas tres abas, no
            desktop e no mobile. O layout solto (sem o agrupamento) saiu. */}
        {(() => {
          const tipoRodadaItem = exibeRunType && opts.tipos_rodada ? (
            <RunType
              opcoes={opts.tipos_rodada}
              valor={tipoRodada}
              onChange={(v) => dispatch(setTipoRodada(v))}
            />
          ) : null;
          // WINDX-061: Modelos e RMV so aparecem DEPOIS que o usuario escolhe
          // o Tipo de Rodada. Os modelos disponiveis dependem de
          // preliminar/final (a rota modelos-disponiveis filtra por
          // tipo_rodada, no SH e — desde o WEOL-SM #99 — no SM/Comparativo) e
          // a consulta exige tipoRodada nas tres abas (selectWindxFiltrosCompletos)
          // — revelar os modelos so apos o pre-requisito reduz a carga cognitiva
          // e elimina o estado intermediario em que o usuario marca modelos que
          // seriam resetados.
          // Sem `tipos_rodada` no painel-options (SM/Comparativo com a flag do
          // BE desligada) nao ha a etapa: Modelos e RMV ficam sempre visiveis e
          // o hook assume o tipo 'final', que libera a consulta.
          const modelosItem = exibeModelosRmv ? (
            <Models
              modelos={modelosVisiveis}
              idsSelecionados={idsModelos}
              onChange={(ids) => dispatch(setIdsModelos(ids))}
              idsIndisponiveis={idsSemDados}
              datasRodadaPorId={datasRodadaPorId}
              tipoRodada={tipoRodada}
              // WINDX-065: o modo deriva da SUB-ABA (painel compartilhado).
              // Semanal (sm) e SH = checkbox (multi-modelo); Comparativo = radio
              // (single-model). Antes (WINDX-028) sm e comparativo eram radio.
              modoSelecao={modoSelecao}
            />
          ) : null;
          const rmvItem = exibeModelosRmv ? (
            <Rmv
              opcoesDisponiveis={opcoesRmvDisponiveis}
              valor={opcoesRmv}
              onChange={(v) => dispatch(setOpcoesRmv(v))}
              apenasComRmv={telaAtiva === 'sm' || telaAtiva === 'comparativo'}
            />
          ) : null;
          // Dica de progressive disclosure: em qualquer aba com a etapa Tipo de
          // Rodada, enquanto ele nao foi escolhido (evita o accordion "vazio"
          // so com radios).
          const dicaTipoRodada = exibeRunType && !tipoRodada ? (
            <p className="text-muted small mb-0 px-3 py-2" data-testid="windx-dica-tipo-rodada">
              Selecione o Tipo de Rodada para escolher os modelos.
            </p>
          ) : null;
          return (
            <EstudoAutomatico>
              {tipoRodadaItem}
              {dicaTipoRodada}
              {modelosItem}
              {rmvItem}
            </EstudoAutomatico>
          );
        })()}

        {exibePatamar && (
          <Patamar
            valor={patamar as PatamarType}
            onChange={(v) => dispatch(setPatamar(v))}
          />
        )}

        {exibeFatorRen && (
          <FatorRen
            valor={fatorRen}
            onChange={(v) => dispatch(setFatorRen(v))}
          />
        )}

        {/* FE-WINDX-043: CRUD de simulacoes antigas nomeadas (substitui
            o datepicker unico). Apenas WEOL-SH; SM/Comparativo sempre
            usam MAX(data_rodada) por modelo. */}
        {exibeSimulacoesAntigas && (
          <SimulacoesAntigasSalvas
            opts={opts}
            dataMinima={dataMinima}
            dataMaxima={dataMaxima}
          />
        )}
      </Accordion>

      {/* WINDX-051: medidor de complexidade (só WEOL-SH). Vermelho desabilita
          "Realizar Consulta" e oferece atalho de agregação. */}
      <MedidorComplexidade
        estimativa={estimativa}
        isFetching={estimativaFetching}
        onAgregar={(subdivisao) => dispatch(setIdSubdivisaoEspacial(subdivisao))}
      />

      <Actions
        onSavePreference={() => setShowSaveForm(true)}
        onRealizarConsulta={() => dispatch(realizarConsulta())}
        onLimparFiltros={() => dispatch(resetWindxControlPanel())}
        filtrosCompletos={filtrosCompletos && estimativa?.veredito !== 'vermelho'}
      />
    </div>
  );
}
